import { DateTime } from 'luxon'
import { useEffect, useMemo, useRef, useState } from 'react'

import { CASCADE_MARKETS, MARKET_TIMEZONE } from '@/features/bidding/constants'
import type { CascadeMarketId, MarketSegmentData, MarketState } from '@/features/bidding/types/market'

interface UseMarketTimelineStateProps {
  selectedDate: DateTime
  bidStatus: Record<CascadeMarketId, boolean>
  clearingPrices?: Partial<Record<CascadeMarketId, string>>
  /** Override current time for testing purposes */
  currentTimeOverride?: DateTime
}

interface UseMarketTimelineStateResult {
  currentTime: DateTime
  markets: MarketSegmentData[]
}

/**
 * Formats the time remaining until gate closure.
 * Shows days if > 24h, otherwise hours and minutes.
 */
function formatCountdown(totalMinutes: number): string {
  if (totalMinutes <= 0) return ''

  const days = Math.floor(totalMinutes / (24 * 60))
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60)
  const mins = totalMinutes % 60

  if (days > 0) {
    if (hours === 0) return `${days}d`
    return `${days}d ${hours}h`
  }
  if (hours === 0) return `${mins}m`
  if (mins === 0) return `${hours}h`
  return `${hours}h ${mins}m`
}

/**
 * Derives market state based on current time and gate closure hour.
 * Gate closure is on D-1 (one day before the delivery day).
 */
function deriveMarketState(
  currentTime: DateTime,
  deliveryDate: DateTime,
  gateHour: number,
): { state: MarketState; minutesRemaining: number } {
  // Gate closure is on D-1 (day before delivery) in market timezone
  const gateClosureTime = deliveryDate
    .setZone(MARKET_TIMEZONE)
    .minus({ days: 1 })
    .startOf('day')
    .set({ hour: gateHour, minute: 0, second: 0 })

  const now = currentTime.setZone(MARKET_TIMEZONE)

  // If current time is before gate closure on D-1
  if (now < gateClosureTime) {
    const diff = gateClosureTime.diff(now, ['minutes'])
    return { state: 'open', minutesRemaining: Math.floor(diff.minutes) }
  }

  return { state: 'closed', minutesRemaining: 0 }
}

/**
 * Hook to derive market timeline state from current time and selected date.
 * Updates every minute to refresh countdowns.
 */
export function useMarketTimelineState({
  selectedDate,
  bidStatus,
  clearingPrices = {},
  currentTimeOverride,
}: UseMarketTimelineStateProps): UseMarketTimelineStateResult {
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [currentTime, setCurrentTime] = useState<DateTime>(
    () => currentTimeOverride ?? DateTime.now().setZone(MARKET_TIMEZONE),
  )

  // Update current time every minute (skip if override provided)
  useEffect(() => {
    if (currentTimeOverride) {
      setCurrentTime(currentTimeOverride)
      return
    }

    const updateTime = () => {
      setCurrentTime(DateTime.now().setZone(MARKET_TIMEZONE))
    }

    // Initial update
    updateTime()

    // Set interval for minute updates
    timerRef.current = setInterval(updateTime, 60 * 1000)

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [currentTimeOverride])

  // Derive market states
  const markets = useMemo((): MarketSegmentData[] => {
    return CASCADE_MARKETS.map((market) => {
      const { state, minutesRemaining } = deriveMarketState(currentTime, selectedDate, market.gateHour)
      const bidSubmitted = bidStatus[market.id] ?? false
      const clearingPrice = clearingPrices[market.id]

      return {
        ...market,
        state,
        bidSubmitted,
        clearingPrice: state === 'closed' && bidSubmitted ? clearingPrice : undefined,
        countdown: state === 'open' ? formatCountdown(minutesRemaining) : undefined,
      }
    })
  }, [currentTime, selectedDate, bidStatus, clearingPrices])

  // Memoize return object per CLAUDE.md patterns
  return useMemo(
    () => ({
      currentTime,
      markets,
    }),
    [currentTime, markets],
  )
}
